import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { IcClose, IcPencil, IcTrash, IcClock, IcCalendar, IcConciergeBell, IcCpu, IcFlower, IcGem, IcUser, IcHistory } from '../../theme/icons';
import serviceManagementService from '../../services/serviceManagementService';
import authService from '../../services/authService';
import { Sk } from '../../components/Skeleton';

// Type de service -> French label, pill colors and icon (mirrors ServiceForm).
const TYPE_META = {
  DIAGNOSTIC: { label: 'Diagnostique', className: 'bdg-type-diag', Icon: IcCpu },
  SOIN: { label: 'Soin', className: 'bdg-type-soin', Icon: IcFlower },
  ACTE: { label: 'Acte', className: 'bdg-type-acte', Icon: IcGem },
};

// Session status -> French label.
const STATUS_LABELS = {
  scheduled: 'Programmée',
  in_progress: 'En cours',
  completed: 'Terminée',
  cancelled: 'Annulée',
};

// Human-readable minutes -> "15 min" / "2 h" / "1 h 30 min".
const formatDuration = (mins) => {
  const m = parseInt(mins, 10);
  if (!m || m <= 0) return null;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h} h ${r} min`;
  if (h) return `${h} h`;
  return `${m} min`;
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// Sessions come back with scheduled_time as "YYYY-MM-DD HH:MM".
const formatSessionDate = (s) => {
  const raw = s.scheduled_time || s.session_date || s.created_at;
  if (!raw) return '—';
  const d = new Date(raw.replace(' ', 'T'));
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatPrice = (value) =>
  typeof value === 'number' ? value.toFixed(3) : (parseFloat(value) || 0).toFixed(3);

// Étiquettes Ki Para : programmé = teinte verte, en cours = gris + texte vert,
// terminé = vert plein, annulé = gris neutre.
const STATUS_BADGE = {
  scheduled: 'bdg-pos',
  in_progress: 'bdg-live',
  completed: 'bdg-done',
  cancelled: 'bdg-neutral',
};

const StatusBadge = ({ status }) => (
  <span className={`bdg ${STATUS_BADGE[status] || 'bdg-neutral'}`}>
    {STATUS_LABELS[status] || status}
  </span>
);

const ServiceDetailModal = ({ service, isAdmin, onClose, onToggleStatus, onDelete, onEdit }) => {
  const [sessions, setSessions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState(null);

  // Fetch this service's session history (role-aware), filtered to this service.
  useEffect(() => {
    if (!service) return;
    let cancelled = false;

    const fetchHistory = async () => {
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const response = authService.isAdmin()
          ? await serviceManagementService.getAllSessions()
          : await serviceManagementService.getSellerSessions();
        const all = response.results || response || [];
        const mine = all
          .filter((s) => String(s.service) === String(service.id))
          .sort((a, b) => new Date((b.scheduled_time || '').replace(' ', 'T')) - new Date((a.scheduled_time || '').replace(' ', 'T')));
        if (!cancelled) setSessions(mine);
      } catch (err) {
        console.error('Error fetching service history:', err);
        if (!cancelled) setHistoryError("Erreur lors du chargement de l'historique");
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };

    fetchHistory();
    return () => { cancelled = true; };
  }, [service]);

  if (!service) return null;

  const type = service.type && TYPE_META[service.type] ? TYPE_META[service.type] : null;
  const TypeIcon = type ? type.Icon : IcConciergeBell;
  const hasRemise = service.remise > 0;
  const finalPrice = service.final_price != null
    ? service.final_price
    : service.price * (1 - (service.remise || 0) / 100);
  const duration = formatDuration(service.duration);

  return (
    <div
      className="modal-backdrop flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      {/* Modale sobre (Brand Kit §05) : rayon 8, ombre, entête encre 13/16,
          titre 600 16px, ✕ 17px gray-400 */}
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-head items-start gap-4 p-6 rounded-t-2xl">
          <div className="h-16 w-16 shrink-0 rounded-2xl overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center shadow-sm">
            {service.image ? (
              <img src={service.image} alt={service.name} className="h-full w-full object-cover" />
            ) : (
              <TypeIcon className="text-2xl" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="modal-title text-xl leading-tight">{service.name}</h2>
              {type && (
                <span className={`bdg ${type.className}`}>{type.label}</span>
              )}
            </div>
            <span
              className={`bdg mt-2 ${service.is_active ? 'bdg-pos' : 'bdg-neutral'}`}
            >
              {service.is_active ? '● Actif' : '○ Inactif'}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white shrink-0" aria-label="Fermer" title="Fermer">
            <IcClose />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Price card */}
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
            <div className="flex items-end justify-between">
              <span className="text-sm text-gray-500">Prix</span>
              <div className="text-right">
                <span className="text-2xl font-bold text-gray-900">{formatPrice(finalPrice)} DT</span>
                {hasRemise && (
                  <div className="flex items-center justify-end gap-2 mt-1">
                    <span className="text-xs text-gray-400 line-through">{formatPrice(service.price)} DT</span>
                    <span className="bdg bdg-promo">−{service.remise}%</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Meta grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                <IcClock />
              </span>
              <div>
                <p className="text-xs text-gray-400">Durée</p>
                <p className="text-sm font-medium text-gray-800">{duration || '—'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
                <IcCalendar />
              </span>
              <div>
                <p className="text-xs text-gray-400">Date de création</p>
                <p className="text-sm font-medium text-gray-800">{formatDate(service.created_at)}</p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <p className="text-xs text-gray-400 mb-1">Description</p>
            <p className="text-sm text-gray-700 whitespace-pre-line">
              {service.description ? service.description : 'Aucune description.'}
            </p>
          </div>

          {/* Session history for THIS service — manager/admin only */}
          {isAdmin && (
          <div className="pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <IcHistory className="text-gray-400" /> Historique des sessions
              </h3>
              {!historyLoading && !historyError && (
                <span className="text-xs font-medium text-gray-400">{sessions.length} session(s)</span>
              )}
            </div>

            {historyLoading ? (
              <div className="flex justify-center py-6">
                <Sk w={40} h={10} className="sk-inline" />
              </div>
            ) : historyError ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                {historyError}
              </div>
            ) : sessions.length === 0 ? (
              <div className="empty-ki py-6">
                <IcHistory size={24} className="empty-ki-icon w-6 h-6 mb-2" />
                <p className="empty-ki-title text-sm mt-0">Aucune session pour ce service.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {sessions.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center gap-3 rounded-xl border border-gray-100 p-3 hover:bg-gray-50 transition-colors"
                  >
                    <span className="h-9 w-9 shrink-0 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                      <IcUser className="text-sm" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {s.customer_name || 'Client inconnu'}
                      </p>
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <IcCalendar className="text-[10px]" /> {formatSessionDate(s)}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <StatusBadge status={s.status} />
                      <p className="text-xs font-medium text-gray-700 mt-1">
                        {formatPrice(s.final_price != null ? s.final_price : s.service_price)} DT
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          )}
        </div>

        {/* Footer — Composants-2 §10 : toujours un bouton de sortie (Fermer ghost),
            + action Modifier (ghost bordé, btn-g-edit) pour l'admin */}
        <div className="flex items-center justify-end gap-2 p-6 pt-0">
          {isAdmin && (
            <>
              <button
                onClick={() => onDelete(service.id)}
                title="Supprimer"
                className="h-11 w-11 flex items-center justify-center rounded-xl text-red-600 hover:bg-red-50 transition-colors mr-auto"
              >
                <IcTrash />
              </button>
              <button
                onClick={() => onToggleStatus(service.id)}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                  service.is_active
                    ? 'border-amber-200 text-amber-700 hover:bg-amber-50'
                    : 'border-green-200 text-green-700 hover:bg-green-50'
                }`}
              >
                {service.is_active ? 'Désactiver' : 'Activer'}
              </button>
            </>
          )}
          <button type="button" onClick={onClose} className="btn-g px-4 py-2.5 text-sm">Fermer</button>
          {isAdmin && (onEdit ? (
            <button type="button" onClick={() => onEdit(service)} className="btn-g-edit px-4 py-2.5 text-sm transition-colors">
              <IcPencil /> Modifier
            </button>
          ) : (
            <Link
              to={`/services/edit/${service.id}`}
              className="btn-g-edit px-4 py-2.5 text-sm transition-colors"
            >
              <IcPencil /> Modifier
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailModal;
